import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  adminGetConditions, adminCreateCondition, adminUpdateCondition, 
  adminDeleteCondition, adminGetSystems 
} from '@/lib/api';
import { toast } from 'sonner';

const AdminConditions = () => {
  const [conditions, setConditions] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [formData, setFormData] = useState({
    system_id: '',
    name: '',
    consumer_label: '',
    practitioner_label: '',
    status: 'active'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [conditionsData, systemsData] = await Promise.all([
        adminGetConditions(),
        adminGetSystems()
      ]);
      setConditions(conditionsData || []);
      setSystems(systemsData || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getSystemName = (systemId) => {
    const system = systems.find(s => s.id === systemId);
    return system?.consumer_label || system?.name || 'Unknown';
  };

  const openCreateDialog = () => {
    setEditingCondition(null);
    setFormData({
      system_id: systems[0]?.id || '',
      name: '',
      consumer_label: '',
      practitioner_label: '',
      status: 'active'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (condition) => {
    setEditingCondition(condition);
    setFormData({
      system_id: condition.system_id,
      name: condition.name,
      consumer_label: condition.consumer_label,
      practitioner_label: condition.practitioner_label || '',
      status: condition.status || 'active'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCondition) {
        await adminUpdateCondition(editingCondition.id, formData);
        toast.success('Condition updated');
      } else {
        await adminCreateCondition(formData);
        toast.success('Condition created');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save condition');
    }
  };

  const handleDelete = async (condition) => {
    if (!window.confirm(`Delete "${condition.consumer_label}"?`)) return;
    try {
      await adminDeleteCondition(condition.id);
      toast.success('Condition deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete condition');
    }
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading conditions...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Conditions</h1>
          <p className="text-stone-600 text-sm">Health conditions linked to protocols</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-condition-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Add Condition
        </Button>
      </div>

      {systems.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <p className="text-amber-800 text-sm">
              Create body systems first before adding conditions.
            </p>
          </CardContent>
        </Card>
      )}

      {conditions.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500 mb-4">No conditions created yet</p>
            <Button onClick={openCreateDialog} variant="outline" className="btn-outline" disabled={systems.length === 0}>
              Create Your First Condition
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {conditions.map((condition) => (
            <Card key={condition.id} className="card-hover" data-testid={`condition-card-${condition.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-[#2C2C2C]">{condition.consumer_label}</h3>
                    <p className="text-xs text-stone-500">{condition.name}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(condition)}
                      className="h-8 w-8 text-stone-400 hover:text-[#4A6763]"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(condition)}
                      className="h-8 w-8 text-stone-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {getSystemName(condition.system_id)}
                  </Badge>
                  <Badge className={condition.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}>
                    {condition.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingCondition ? 'Edit Condition' : 'Add Condition'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Body System</label>
                <Select
                  value={formData.system_id}
                  onValueChange={(value) => setFormData({ ...formData, system_id: value })}
                >
                  <SelectTrigger data-testid="condition-system-select">
                    <SelectValue placeholder="Select system" />
                  </SelectTrigger>
                  <SelectContent>
                    {systems.map((system) => (
                      <SelectItem key={system.id} value={system.id}>
                        {system.consumer_label || system.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Internal Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., poor_digestion"
                  required
                  data-testid="condition-name-input"
                />
              </div>
              <div>
                <label className="form-label">Consumer Label</label>
                <Input
                  value={formData.consumer_label}
                  onChange={(e) => setFormData({ ...formData, consumer_label: e.target.value })}
                  placeholder="e.g., Digestive Support"
                  required
                  data-testid="condition-label-input"
                />
              </div>
              <div>
                <label className="form-label">Practitioner Label (Optional)</label>
                <Input
                  value={formData.practitioner_label}
                  onChange={(e) => setFormData({ ...formData, practitioner_label: e.target.value })}
                  placeholder="e.g., GI Dysfunction"
                  data-testid="condition-practitioner-input"
                />
              </div>
              <div>
                <label className="form-label">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="condition-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-condition-btn">
                {editingCondition ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminConditions;
